import { useRef, useState, useMemo, useCallback } from 'react'
import { useWireState } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import { randomNumber } from '@utils'
import useScrollToTop from '@hook/useScrollToTop'
import { PENDO_EVENTS, pendoTrack } from '@/utils/pendo'

const BATCH_COUNT = 10
const SIMULATE_UPLOADS = false

const UploadChecksDialogViewModel = onClose => {
    
    const refUploads = useRef()
    
    const [error, setError] = useState(null)
    const [files, setFiles] = useState([])
    const [isUploading, setIsUploading] = useState(false)
    const [fileUploads, setFileUploads] = useState({})
    const [completedCount, setCompletedCount] = useState(0)
    const [batchedFiles, setBatchedFiles] = useState([])
    const [nameOverrides, setNameOverrides] = useState([])
    
    const [open, setOpen] = useWireState(store.uploadChecksDialogOpen)
    const organizationId = store.currentOrganization.getValue().id
    
    // @todo prob won't need this once we fix styling in the dialog
    // currently the whole dialog scrolls, but should just be the files table
    const scrollToTop = useScrollToTop(refUploads)
    
    const resetState = () => {
        setError(null)
        setFiles([])
        setIsUploading(false)
        setFileUploads({})
        setCompletedCount(0)
        setNameOverrides([])
    }
    
    const closeDialog = () => {
        resetState()
        onClose()
        setOpen(false)
    }
    
    const onDrop = async files => {
        
        try {
            
            const validFiles = files.filter(it => it.name?.length)
            
            if (validFiles.length)
                setFiles(validFiles)
            
        } catch (e) {
            console.error('onDrop failed', e)
        }
        
    }
    
    const updateFileProgress = (name, max = null, value = null) => {
        
        // console.log('updateFileProgress', name, value, '/', max)
        
        setFileUploads(state => ({
            ...state,
            [name]: {
                max: max !== null ? max : state[name]?.max || 0,
                value: value !== null ? value : state[name]?.value || 0,
            },
        }))
        
    }
    
    const uploadCheck = async file => {
        
        updateFileProgress(file.name)
        
        // @todo allow form edit names
        const name = file.name.toString()
        
        if (SIMULATE_UPLOADS) {
            
            let _max = 100
            let _val = 0
            
            updateFileProgress(file.name, _max)
            
            while (_val < _max) {
                await new Promise(x => setTimeout(x, randomNumber(300, 900)))
                _val = Math.min(_val + randomNumber(3, 33), _max)
                updateFileProgress(file.name, _max, _val)
            }
            
            setCompletedCount(prev => prev + 1)
            
            return
            
        }
        
        const checkId = await actions.createCheck(
            name,
            file,
            value => updateFileProgress(file.name, value), 
            value => updateFileProgress(file.name, null, value),
        )
        
        pendoTrack(PENDO_EVENTS.CHECK_UPLOAD, { organizationId, checkId, fileName: file.name, fileSize: file.size })
        
    }
    
    const batchuploadChecks = async sourceFiles => {
        
        const files = [...sourceFiles]
        
        if (files.length <= BATCH_COUNT) {
            setBatchedFiles(files)
            return await Promise.all(files.map(it => uploadCheck(it)))
        }
        
        let results = []
        
        setCompletedCount(0)
        
        while (files.length) {
            
            const batch = files.splice(0, BATCH_COUNT)
            
            setBatchedFiles(batch)
            
            const result = await Promise.all(batch.map(it => uploadCheck(it)))
            
            results = [...results, ...result]
            
        }
        
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        setBatchedFiles([])
        setCompletedCount(0)
        
        return results
        
    }
    
    const updateNameOverride = (e, index) => setNameOverrides(prev => {
        return prev.map((it, i) => i === index ? e.target.value : it)
    })
    
    const handleSubmit = useCallback(async e => {
        
        e.preventDefault()
        
        scrollToTop()
        setIsUploading(true)
        
        await batchuploadChecks(files)
        
        setIsUploading(false)
        setFileUploads({})
        resetState()
        closeDialog()
        
    })
    
    const getProgressWidth = fileUpload => {
        
        if (!fileUpload) return '0%'
        
        return ((fileUpload?.value / fileUpload?.max) * 100) + '%'
        
    }
    
    const totalProgressWidth = useMemo(() => {
        
        if (!files.length || completedCount < 1) return '0%'
        
        return ((completedCount / files.length) * 100) + '%'
        
    }, [files, completedCount])
    
    const state = useMemo(() => {
        
        if (error !== null) return 'error'
        if (!isUploading && files.length < 1) return 'ready'
        if (!isUploading && files.length > 0) return 'edit'
        if (isUploading && files.length > 0) return 'uploading'
        
        throw new Error('Unknown state')
        
    }, [error, isUploading, files])
    
    return {
        BATCH_COUNT,
        refUploads,
        error,
        setError,
        open,
        setOpen,
        files,
        onDrop,
        isUploading,
        setIsUploading,
        fileUploads,
        batchedFiles,
        completedCount,
        nameOverrides,
        updateNameOverride,
        getProgressWidth,
        totalProgressWidth,
        closeDialog,
        handleSubmit,
        state,
    }
    
}

export default UploadChecksDialogViewModel
